import { deliveredOnDay } from './dates';

export const orderTotal = (order) => Number(order?.total) || Number(order?.subtotal) || 0;

export const isCod = (order) => {
  const mode = (order.paymentMode || 'cash').toLowerCase();
  return mode === 'cash' || mode === 'cod';
};

export const paymentSplit = (orders) => {
  let cod = { count: 0, total: 0 };
  let online = { count: 0, total: 0 };
  orders.forEach((o) => {
    const bucket = isCod(o) ? cod : online;
    bucket.count += 1;
    bucket.total += o.total || 0;
  });
  return { cod, online };
};

export const cancellationSummary = (orders) => {
  const cancelled = orders.filter((o) => o.status === 'cancelled');
  const byReason = {};
  cancelled.forEach((o) => {
    const reason = o.cancellationReason || 'unspecified';
    if (!byReason[reason]) byReason[reason] = { count: 0, orders: [] };
    byReason[reason].count += 1;
    byReason[reason].orders.push(o);
  });
  return {
    total: cancelled.length,
    byReason: Object.entries(byReason)
      .map(([reason, data]) => ({ reason, count: data.count }))
      .sort((a, b) => b.count - a.count),
  };
};

export const searchOrders = (orders, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return orders;
  return orders.filter((o) => {
    const id = (o.id || '').toLowerCase();
    const num = o.orderNumber != null ? String(o.orderNumber) : '';
    const phone = (o.customerPhone || '').toLowerCase();
    const shop = (o.shopName || '').toLowerCase();
    return id.includes(q) || num.includes(q) || phone.includes(q) || shop.includes(q);
  });
};

export const riderEarnings = (orders, dayKey) => {
  const map = {};
  orders.forEach((o) => {
    if (dayKey && !deliveredOnDay(o, dayKey)) return;
    if (o.status !== 'delivered' || !o.deliveryPartnerId) return;
    const id = o.deliveryPartnerId;
    if (!map[id]) map[id] = { deliveries: 0, fees: 0, name: o.deliveryPartnerName || '' };
    map[id].deliveries += 1;
    map[id].fees += o.deliveryFee || 0;
    if (o.deliveryPartnerName) map[id].name = o.deliveryPartnerName;
  });
  return map;
};

/** Per-rider COD vs online totals for delivered orders on a given day */
export const riderDailyCollection = (orders, dayKey) => {
  const map = {};
  orders.forEach((o) => {
    if (o.status !== 'delivered' || !o.deliveryPartnerId) return;
    if (dayKey && !deliveredOnDay(o, dayKey)) return;
    const id = o.deliveryPartnerId;
    if (!map[id]) {
      map[id] = {
        deliveries: 0,
        cod: { count: 0, total: 0 },
        online: { count: 0, total: 0 },
        name: o.deliveryPartnerName || '',
        orders: [],
      };
    }
    const entry = map[id];
    entry.deliveries += 1;
    const amount = orderTotal(o);
    const bucket = isCod(o) ? entry.cod : entry.online;
    bucket.count += 1;
    bucket.total += amount;
    if (o.deliveryPartnerName) entry.name = o.deliveryPartnerName;
    entry.orders.push(o);
  });
  Object.values(map).forEach((entry) => {
    entry.orders.sort((a, b) => {
      const ta = a.deliveredAt?.toDate?.()?.getTime() || 0;
      const tb = b.deliveredAt?.toDate?.()?.getTime() || 0;
      return tb - ta;
    });
  });
  return map;
};

export const buildStatusTimeline = (order) => {
  const steps = [];
  const add = (status, label, ts) => {
    if (ts?.toDate) steps.push({ status, label, at: ts.toDate() });
  };
  add('created', 'Order placed', order.createdAt);
  if (order.status === 'payment_pending' || order.paymentStatus === 'paid') {
    add('payment', 'Payment', order.paidAt || order.updatedAt);
  }
  if (['confirmed', 'picked_up', 'delivered', 'cancelled'].includes(order.status)) {
    add('confirmed', 'Confirmed', order.confirmedAt || order.updatedAt);
  }
  if (order.deliveryPartnerId) {
    add('assigned', `Rider: ${order.deliveryPartnerName || order.deliveryPartnerId.slice(0, 8)}`, order.assignedAt);
  }
  if (['picked_up', 'delivered'].includes(order.status)) {
    add('picked_up', 'Picked up', order.pickedUpAt || order.updatedAt);
  }
  if (order.status === 'delivered') {
    add('delivered', 'Delivered', order.deliveredAt || order.updatedAt);
  }
  if (order.status === 'cancelled') {
    add('cancelled', order.cancellationReason ? `Cancelled (${order.cancellationReason})` : 'Cancelled', order.cancelledAt || order.updatedAt);
  }
  if (order.status === 'failed') {
    add('failed', 'Payment failed', order.failedAt || order.updatedAt);
  }
  return steps.sort((a, b) => (a.at?.getTime() || 0) - (b.at?.getTime() || 0));
};

export const computeAnalytics = (orders) => {
  const delivered = orders.filter((o) => o.status === 'delivered');
  const customerCounts = {};
  delivered.forEach((o) => {
    const id = o.customerId || o.customerPhone || 'unknown';
    customerCounts[id] = (customerCounts[id] || 0) + 1;
  });
  const repeatCustomers = Object.values(customerCounts).filter((c) => c > 1).length;
  const totalCustomers = Object.keys(customerCounts).length;

  const hourCounts = Array.from({ length: 24 }, (_, h) => ({ hour: h, count: 0 }));
  orders.forEach((o) => {
    const t = o.createdAt?.toDate?.();
    if (t) hourCounts[t.getHours()].count += 1;
  });
  const peakHours = [...hourCounts].sort((a, b) => b.count - a.count).slice(0, 5);

  const productMap = {};
  delivered.forEach((o) => {
    (o.items || []).forEach((item) => {
      const key = item.productId || item.name || 'unknown';
      if (!productMap[key]) productMap[key] = { name: item.name || key, qty: 0, revenue: 0 };
      productMap[key].qty += item.qty || 1;
      productMap[key].revenue += (item.price || 0) * (item.qty || 1);
    });
  });
  const topProducts = Object.values(productMap).sort((a, b) => b.qty - a.qty).slice(0, 10);

  return { repeatCustomers, totalCustomers, peakHours, topProducts, orderCount: orders.length, deliveredCount: delivered.length };
};
